import { useEffect, useState } from 'react';
import { TextInput } from '@components/shared/form/TextInput';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { useFormContext } from '@context/FormContext';
import { PhoneTextInput } from '@components/shared/form/PhoneTextInput';
import { countryStates } from '@shared/constants/countryStates';
import { SSNInput } from '@components/shared/form/SSNInput';
import useForm, { validate } from '@shared/hooks/useForm';
import { useProve } from '@context/proveProvider';
import { complete } from '@services/prove';
import { createContact } from '@services/verifyYourInformation';
import { CDX_ACTIONS, IVerifyYourInfoManuallyAddress, IVerifyYourInformation, UpdateAddressInfo } from 'types/form';
import { createErrorResponse, formatPostalCode } from '@services/utils';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { SECONDARY_SOURCE, UNIDENTIFIED_SOURCE } from '@shared/constants/utm';
import { Address } from 'types/proveIdentity';
import { RESIDENT_TYPE_OPTIONS } from '@shared/constants/misc';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { setLocalStorage } from 'utils/setLocalStorage';
import { convertArrayToSnakeCase } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

type YourInfoManuallyForm = Omit<IVerifyYourInformation, 'addresses'> & IVerifyYourInfoManuallyAddress;

const YourInformationManuallyForm = () => {
  const { t } = useTexts();
  const { trackClick, trackFormSubmit, trackPage, identifyUser, startInteraction } = useAnalytics(
    SEGMENT_SCREEN_PARAMS.CONTACT_INFO_MANUAL
  );
  const {
    currentStep,
    licensePlateInfoForm,
    manualLicensePlateInfoForm,
    verifyYourInformation,
    verifyYourIdentity,
    licensePlateInfoResponse,
    updateVerifyYourInformationForm,
    dispatch,
    isLoading,
    widgetInitVariables,
    contactDetails,
  } = useFormContext();
  const { correlationId } = useProve();
  const { next } = useScreenNavigator();
  const [isSSNDisabled, setIsSSNDisabled] = useState(false);
  const { errors, handleChange, handleSubmit } = useForm<YourInfoManuallyForm>({
    initialValues: {
      firstName: verifyYourInformation?.firstName,
      lastName: verifyYourInformation?.lastName,
      streetAddress: verifyYourInformation.addresses?.[0]?.address || '',
      extendedAddress: verifyYourInformation.addresses?.[0]?.extendedAddress || '',
      city: verifyYourInformation.addresses?.[0]?.city || '',
      state: verifyYourInformation.addresses?.[0]?.region || '',
      postalCode: formatPostalCode(verifyYourInformation.addresses?.[0]?.postalCode) || '',
      phone: verifyYourIdentity.phone,
      ssn: verifyYourInformation.ssn,
      dateOfBirth: verifyYourInformation.dateOfBirth,
      email: verifyYourInformation.email,
      residency: verifyYourInformation?.residency,
    },
    validationSchema: {
      firstName: validate.string('firstName').required(t('yourInformationManually.validation.firstName')),
      lastName: validate.string('lastName').required(t('yourInformationManually.validation.lastName')),
      streetAddress: validate.string('streetAddress').required(t('yourInformationManually.validation.streetAddress')),
      extendedAddress: validate.string('extendedAddress'),
      city: validate.string('city').required(t('yourInformationManually.validation.city')),
      state: validate.string('state').required(t('yourInformationManually.validation.state')),
      postalCode: validate
        .string('postalCode')
        .required(t('yourInformationManually.validation.postalCode.required'))
        .matches(/^\d{5}$/, t('yourInformationManually.validation.postalCode.validPostalCode')),
      phone: validate
        .string('phone')
        .required(t('yourInformationManually.validation.phone.required'))
        .matches(/^\d{10}$/, t('yourInformationManually.validation.phone.validPhoneNumber')),
      ssn: validate
        .string('ssn')
        .required(t('yourInformationManually.validation.ssn.required'))
        .matches(/^(?:\d{3}-\d{2}-\d{4}|XXX-XX-\d{4})$/, t('yourInformationManually.validation.ssn.validSsn')),
      dateOfBirth: validate.string('dateOfBirth').required(t('yourInformationManually.validation.dateOfBirth')).isAdult(),
      email: validate
        .string('email')
        .required(t('yourInformationManually.validation.email.required'))
        .email(t('yourInformationManually.validation.email.validEmail')),
      residency: validate.string('residency').required(t('yourInformationManually.validation.residency.required')),
    },
    onSubmit: async (values) => {
      const { firstName, lastName, streetAddress, city, state, postalCode, extendedAddress, ssn, dateOfBirth, email } = values;

      dispatch({ type: 'SUBMIT_START' });
      try {
        const response = await complete({
          individual: {
            firstName: firstName,
            lastName: lastName,
            ssn: ssn,
            addresses: [
              {
                city: city,
                address: streetAddress,
                extendedAddress: extendedAddress,
                postalCode: postalCode,
                region: state,
              },
            ],
            emailAddresses: [email],
            dob: dateOfBirth,
          },
          correlationId: correlationId as string,
        });
        if ('error' in response) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: response,
          });
        } else {
          if (response?.success === false) {
            next(currentStep, 'postSubmitOutcome', { completeResponse: response }, true);
          }
          if (response && response.success) {
            try {
              const urlParams = widgetInitVariables?.tracking?.extraData && JSON.parse(widgetInitVariables?.tracking?.extraData);

              const defaultUtm = {
                primarySourceId: widgetInitVariables?.PartnerName || urlParams?.partner_name || UNIDENTIFIED_SOURCE,
                secondarySourceId: SECONDARY_SOURCE,
              };
              trackClick('UTM Parameters', defaultUtm);

              const deviceType = window.innerWidth <= 500 ? 'mobile' : 'desktop';
              const metaData = {
                version: '1.0',
                loanApplicationId: urlParams?.loan_application_id || '',
                partnerName: widgetInitVariables?.PartnerName || urlParams?.partner_name || UNIDENTIFIED_SOURCE,
                secondaryLeadSource: SECONDARY_SOURCE,
                utm: {
                  utm_medium: urlParams?.utm_medium || '',
                  utm_campaign: urlParams?.utm_campaign || '',
                  utm_content: urlParams?.utm_content || '',
                  secondary_source: urlParams?.secondary_source || '',
                  gclid: urlParams?.gclid || '',
                  subid: urlParams?.subid || '',
                  clickref: urlParams?.clickref || '',
                  search_id: urlParams?.search_id || '',
                  oneToOneConsent: 'true',
                  subid1: urlParams?.subid1 || '',
                  subid2: urlParams?.subid2 || '',
                  subid3: deviceType || '', // will be updated below with device type
                  sharedid: urlParams?.sharedid || '',
                  clickid: urlParams?.clickid || '',
                  arm_enabled: urlParams?.arm_enabled || '',
                  utm_source: urlParams?.utm_source || '',
                  irclickid: urlParams?.irclickid || '',
                },
              };

              const createContactResponse = await createContact(
                contactDetails?.contactId || null,
                contactDetails?.leadId || null,
                contactDetails?.assetId || null,
                licensePlateInfoForm,
                manualLicensePlateInfoForm,
                verifyYourInformation,
                verifyYourIdentity,
                licensePlateInfoResponse,
                metaData,
                correlationId || null
              );
              const userObject = {
                email: email,
                first_name: firstName,
                last_name: lastName,
                phone: verifyYourInformation?.phone,
                loan_application_id: urlParams?.loan_application_id || '',
                resident_status: verifyYourInformation?.residency,
                plateState: licensePlateInfoForm.plateState,
                plateNumber: licensePlateInfoForm.plateNumber,
                estimatedMileageRange: licensePlateInfoForm.estimatedMileageRange || manualLicensePlateInfoForm.estimatedMileage,
                vin: licensePlateInfoResponse?.vin ?? '',
                year: manualLicensePlateInfoForm.year,
                make: manualLicensePlateInfoForm.makeName || manualLicensePlateInfoForm.make,
                model: manualLicensePlateInfoForm.model,
              };

              if ('error' in createContactResponse) {
                trackFormSubmit('Form Submitted', {
                  submission_status: false,
                  value: 0,
                  currency: 'USD',
                  ...userObject,
                  ...defaultUtm,
                });
                dispatch({
                  type: CDX_ACTIONS.SUBMIT_API_ERROR,
                  payload: createContactResponse,
                });
              } else {
                dispatch({ type: CDX_ACTIONS.SUBMIT_CREATE_CONTACT_SUCCESS, payload: createContactResponse ?? null });

                let updatedAddress: UpdateAddressInfo[] = [];
                if (Array.isArray(verifyYourInformation?.addresses) && verifyYourInformation?.addresses?.length > 0) {
                  const addressFromStore = verifyYourInformation?.addresses;
                  updatedAddress = addressFromStore.map((addr: Address, index: number) => ({
                    streetLine1: addr?.address ?? '',
                    streetLine2: addr?.extendedAddress ?? '',
                    city: addr?.city,
                    state: addr?.region,
                    zip: addr?.postalCode,
                    type: '',
                    monthlyPayment: 0,
                    monthsAtAddress: 0,
                    unit: '',
                    county: null,
                    isCurrent: index === 0 ? true : false,
                  }));
                }
                const contactData = {
                  addresses: updatedAddress,
                  contactId: createContactResponse.contactId,
                  leadId: createContactResponse.leadId,
                  assetId: createContactResponse?.assetId,
                  timeStamp: new Date().toISOString(),
                };
                setLocalStorage(contactData, 'contactData');

                trackFormSubmit('Form Submitted', {
                  submission_status: true,
                  prefill_status: 'edited',
                  forward_screen_name: 'loan_details',
                  ...userObject,
                  ...defaultUtm,
                  ...contactData,
                });
                trackFormSubmit('Consent Update Provided', {
                  form_context: 'contact_info',
                  consent_type: 'add', // add|remove
                  consent_category: 'marketing',
                  one_to_one_consent: true,
                  submission_status: true,
                  ...userObject,
                  ...defaultUtm,
                  ...contactData,
                });
                trackFormSubmit('Lead Created', {
                  prefill_status: 'accepted',
                  one_to_one_consent: true,
                  soft_pull_authorization_consent: true,
                  submission_status: true,
                  value: 150,
                  currency: 'USD',
                  ...userObject,
                  ...defaultUtm,
                  ...contactData,
                });
                identifyUser(createContactResponse?.contactId, userObject);
                next(currentStep, 'postSubmitOutcome', { createContactResponse }, true);
              }
            } catch (error) {
              dispatch({
                type: CDX_ACTIONS.SUBMIT_API_ERROR,
                payload: createErrorResponse(
                  error instanceof Error ? error.name : 400,
                  error instanceof Error ? error.message : ''
                ),
              });
            }
          } else {
            trackFormSubmit('Form Submitted', {
              submission_status: false,
              prefill_status: 'edited',
              submission_fail_reason: 'mismatch',
            });
          }
        }
      } catch (error) {
        trackFormSubmit('Form Submitted', {
          submission_status: false,
          prefill_status: 'edited',
          submission_fail_reason: error instanceof Error ? error.message : '',
        });
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : ''),
        });
      }
    },
    onValidationError: (errors) => {
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        prefill_status: 'edited',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  useEffect(() => {
    if (!verifyYourInformation.firstName) {
      trackPage(t('pageTitles.EnterYourInformation'), { prefill_status: 'edited', prefill_source: 'credit_bureau' });
    } else {
      trackPage(t('pageTitles.EditYourInformation'), { prefill_status: 'edited', prefill_source: 'credit_bureau' });
    }
  }, []);

  useEffect(() => {
    if (verifyYourInformation?.ssn) {
      setIsSSNDisabled(true);
    }
  }, []);

  useEffect(() => {
    handleChange('phone', verifyYourIdentity.phone);
    handleChange('dateOfBirth', verifyYourIdentity.dateOfBirth);

    updateVerifyYourInformationForm({ phone: verifyYourIdentity.phone, dateOfBirth: verifyYourIdentity.dateOfBirth });
  }, [verifyYourIdentity.phone, verifyYourIdentity.dateOfBirth]);

  const handleInputChange = <K extends keyof YourInfoManuallyForm | keyof Address>(field: K, value: string) => {
    const addressFields = ['address', 'extendedAddress', 'city', 'region', 'postalCode'];
    if (addressFields.includes(field as string)) {
      const existingAddresses = verifyYourInformation.addresses ?? [];
      const firstAddress = existingAddresses[0] ?? {};
      const remainingAddresses = existingAddresses.slice(1);

      const updatedAddress = { ...firstAddress, [field]: value };
      updateVerifyYourInformationForm({
        addresses: [updatedAddress, ...remainingAddresses],
      });

      if (field === 'address') {
        handleChange('streetAddress', value);
      } else if (field === 'region') {
        handleChange('state', value);
      } else {
        handleChange(field, value);
      }
    } else {
      if (field !== 'address' && field !== 'region') {
        handleChange(field, value);
        updateVerifyYourInformationForm({ [field]: value });
      }
    }
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        onFocus={startInteraction}
      >
        <h1 className="iLendingCDX-form-title">{t('yourInformationManually.title')}</h1>

        <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-4 ">{t('yourInformationManually.pleaseReviewAndVerify')}</h2>
        <div>
          <TextInput
            label={t('yourInformationManually.form.firstName')}
            className={'iLendingCDX-mb-6'}
            name="firstName"
            value={verifyYourInformation.firstName}
            errorMessage={errors.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
          />

          <TextInput
            label={t('yourInformationManually.form.lastName')}
            className={'iLendingCDX-mb-6'}
            name="lastName"
            value={verifyYourInformation.lastName}
            errorMessage={errors.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
          />

          <TextInput
            label={t('yourInformationManually.form.streetAddress')}
            className={'iLendingCDX-mb-6'}
            name="address"
            value={verifyYourInformation.addresses?.[0]?.address || ''}
            errorMessage={errors.streetAddress}
            onChange={(e) => handleInputChange('address', e.target.value)}
          />
          <TextInput
            label={t('yourInformationManually.form.extendedAddress')}
            className={'iLendingCDX-mb-6'}
            name="extendedAddress"
            value={verifyYourInformation.addresses?.[0]?.extendedAddress || ''}
            errorMessage={errors.extendedAddress}
            onChange={(e) => handleInputChange('extendedAddress', e.target.value)}
          />
          <TextInput
            label={t('yourInformationManually.form.city')}
            className={'iLendingCDX-mb-6'}
            name="city"
            value={verifyYourInformation.addresses?.[0]?.city || ''}
            errorMessage={errors.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
          />
          <CustomSelect
            label={t('yourInformationManually.form.state')}
            className={'iLendingCDX-mb-6'}
            options={countryStates}
            value={verifyYourInformation.addresses?.[0]?.region || ''}
            errorMessage={errors.state}
            onChange={(e) => handleInputChange('region', e.target.value)}
          />
          <TextInput
            label={t('yourInformationManually.form.postalCode')}
            className={'iLendingCDX-mb-6'}
            name="postalCode"
            value={formatPostalCode(verifyYourInformation.addresses?.[0]?.postalCode) || ''}
            errorMessage={errors.postalCode}
            onChange={(e) => handleInputChange('postalCode', formatPostalCode(e.target.value))}
          />
          <PhoneTextInput
            disabled
            label={t('yourInformationManually.form.phone')}
            className={'iLendingCDX-mb-6'}
            name="phone"
            value={verifyYourIdentity.phone}
            errorMessage={errors.phone}
            onChange={(value) => handleInputChange('phone', value as IVerifyYourInformation['phone'])}
          />

          <SSNInput
            disabled={isSSNDisabled}
            label={t('yourInformationManually.form.ssn')}
            className={'iLendingCDX-mb-6'}
            name="ssn"
            value={verifyYourInformation.ssn}
            errorMessage={errors.ssn}
            onChange={(value) => handleInputChange('ssn', value as IVerifyYourInformation['ssn'])}
          />
          <TextInput
            disabled={!!verifyYourInformation.dateOfBirth}
            label={t('yourInformationManually.form.dateOfBirth')}
            className={'iLendingCDX-mb-6'}
            name="dateOfBirth"
            type="date"
            max="9999-12-31"
            value={verifyYourInformation.dateOfBirth}
            errorMessage={errors.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
          />
          <div className="iLendingCDX-footer">
            <span className="iLendingCDX-footer-divider--full"></span>
          </div>
          <h1 className="iLendingCDX-form-title">{t('yourInformation.title2')}</h1>

          <p className="iLendingCDX-form-title--small"> {t('yourInformation.enterCurrentEmail')}</p>

          <div className="iLendingCDX-mb-6 iLendingCDX-mt-6">
            <TextInput
              label={t('yourInformation.form.email')}
              className={'iLendingCDX-mb-6'}
              name="email"
              value={verifyYourInformation.email}
              errorMessage={errors.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
            <CustomSelect
              label={t('yourInformation.form.residency')}
              className={'iLendingCDX-mb-6'}
              options={RESIDENT_TYPE_OPTIONS}
              value={verifyYourInformation.residency || ''}
              errorMessage={errors.residency}
              onChange={(e) => handleInputChange('residency', e.target.value)}
            />

            <label
              className="iLendingCDX-checkbox-wrap-container iLendingCDX-input-label iLendingCDX-input-label--check"
              htmlFor="consent-check"
            >
              {t('yourInformation.acknowledge')}{' '}
              {t('yourInformation.confirmText')}
              <a
                href="https://ilendingcarloanrefinancing.com/sms-terms-of-use"
                target="_blank"
                rel="noreferrer"
              >
                {t('yourInformation.smsTerms')}
                {','}
              </a>
              <a
                href="https://ilendingcarloanrefinancing.com/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                {t('yourInformation.privacyPolicy')}
              </a>
              {t('yourInformation.and')}
              <a
                href="https://ilendingcarloanrefinancing.com/terms-of-use"
                target="_blank"
                rel="noreferrer"
              >
                {t('yourInformation.termsOfUse')}
              </a>
              {t('yourInformation.consentNotRequired')}
            </label>
          </div>
          <Button
            type="submit"
            className="iLendingCDX-button--rounded"
            size="lg"
            isLoading={isLoading}
          >
            {t('yourInformationManually.form.continue')}
          </Button>
        </div>
      </form>
    </div>
  );
};
export default YourInformationManuallyForm;
